import React from 'react';
import { Quote } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { successStoriesData } from '../data/mock';

const SuccessStories = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img loading="lazy"
            src="https://images.pexels.com/photos/9479821/pexels-photo-9479821.jpeg"
            alt="Success celebration"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-gray-900/75"></div>
        </div>
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Stories of <span className="text-orange-400">Transformation</span>
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Real people. Real change. Real hope. Meet some of the incredible individuals 
              who have transformed their lives through our programs.
            </p>
          </div>
        </div>
      </section>

      {/* Impact Statement */}
      <section className="py-16 bg-gradient-to-b from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-2xl text-gray-700 leading-relaxed font-light">
              "Every story represents not just one life changed, but entire families restored, 
              communities strengthened, and futures rewritten. These are the faces of resilience, 
              courage, and hope."
            </p>
            <div className="mt-6 text-orange-600 font-semibold">
              — David's Harmony House Foundation Team
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto space-y-16">
            {successStoriesData.map((story, index) => (
              <Card 
                key={story.id} 
                className="border-0 shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300"
              >
                <div className={`grid md:grid-cols-5 gap-0`}>
                  {/* Story Content */}
                  <CardContent className="md:col-span-3 p-8 md:p-12 flex flex-col justify-center">
                    <div className="inline-block mb-4">
                      <span className="px-4 py-2 bg-orange-100 text-orange-700 text-sm font-semibold rounded-full">
                        {story.program}
                      </span>
                    </div>
                    
                    <Quote className="w-12 h-12 text-orange-300 mb-4" />
                    
                    <blockquote className="text-xl text-gray-700 leading-relaxed mb-6 italic">
                      "{story.quote}"
                    </blockquote>
                    
                    <div className="border-l-4 border-orange-600 pl-6 mb-6">
                      <div className="text-2xl font-bold text-gray-900 mb-1">
                        {story.name}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">
                        {story.program}
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-5 rounded-lg">
                      <div className="text-sm text-gray-600 font-medium mb-2">
                        Current Status:
                      </div>
                      <div className="text-lg font-bold text-orange-700">
                        {story.outcome}
                      </div>
                    </div>
                  </CardContent>

                  {/* Visual Element */}
                  <div className={`md:col-span-2 relative min-h-[300px] md:min-h-0 bg-gradient-to-br ${
                    index % 4 === 0 ? 'from-orange-500 to-amber-600' :
                    index % 4 === 1 ? 'from-amber-500 to-orange-500' :
                    index % 4 === 2 ? 'from-orange-600 to-red-600' :
                    'from-amber-600 to-orange-700'
                  }`}>
                    <div className="absolute inset-0 flex items-center justify-center p-8">
                      <div className="text-center text-white">
                        <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white/40">
                          <span className="text-4xl font-bold">
                            {story.name.charAt(0)}
                          </span>
                        </div>
                        <div className="text-sm font-medium opacity-90">
                          Program Participant
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Our <span className="text-orange-600">Collective Impact</span>
              </h2>
              <p className="text-xl text-gray-600">
                Behind every number is a person whose life has been transformed
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="border-0 shadow-lg text-center">
                <CardContent className="p-8">
                  <div className="text-5xl font-bold text-orange-600 mb-3">500+</div>
                  <div className="text-sm text-gray-600 font-medium">Lives Transformed</div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg text-center">
                <CardContent className="p-8">
                  <div className="text-5xl font-bold text-orange-600 mb-3">85%</div>
                  <div className="text-sm text-gray-600 font-medium">Housing Success Rate</div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg text-center">
                <CardContent className="p-8">
                  <div className="text-5xl font-bold text-orange-600 mb-3">72%</div>
                  <div className="text-sm text-gray-600 font-medium">Employment Rate</div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg text-center">
                <CardContent className="p-8">
                  <div className="text-5xl font-bold text-orange-600 mb-3">95%</div>
                  <div className="text-sm text-gray-600 font-medium">Program Satisfaction</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Your Story Could Be Next
            </h2>
            <p className="text-xl mb-8 text-orange-100 leading-relaxed">
              If you or someone you know needs support, we're here to help write the next chapter 
              of hope and transformation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/get-help">
                <Button 
                  size="lg"
                  className="bg-white text-orange-600 hover:bg-gray-100 font-semibold text-lg px-8 shadow-xl"
                >
                  Get Help Now
                </Button>
              </a>
              <a href="/get-involved">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-orange-600 font-semibold text-lg px-8"
                >
                  Help Others
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Note */}
      <section className="py-8 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm text-gray-600">
              <strong>Privacy Note:</strong> All stories shared with permission. Names may be changed 
              to protect privacy. Photos are representative and may not depict actual program participants.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SuccessStories;
